/* screens/checkout/CheckoutMock.jsx — заглушка платёжного шлюза (S11).
   /checkout/mock/:invoiceId — детали платежа + «Симулировать оплату»/«Отказаться».
   Состояния: loading / error(404) / already_paid / already_closed / pending / busy.
   Источник: 12 §2.1, Сценарий 7. ХЕНДОФФ: → pages/checkout/CheckoutMock.jsx. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { hooks } from '../../hooks/index.jsx';
import { PLAN_LABELS, KIND_LABELS } from '../../lib/adapters/checkout.js';
import { subscriptionReal } from '../../lib/adapters/subscription.js';
import { fmt } from '../../lib/format.js';
import { RR } from '../../lib/router.jsx';
import { CheckoutLayout } from './CheckoutLayout.jsx';
import { CheckoutRow } from './CheckoutRow.jsx';

  const { useState } = React;
  const { useParams, useNavigate, useSearchParams } = RR;
  const { useFakeQuery } = hooks;

  function CheckoutMock() {
    const { invoiceId } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    // ws — счёт бизнес-пространства (не активного): getPaymentStatus скоупится по нему через X-Workspace-Id.
    // Личный поток без ?ws → undefined → активный ws (как раньше).
    const ws = params.get('ws') || undefined;
    // Токен именной ссылки: стаб-страница, как и боевые страницы возврата,
    // обязана открываться у постороннего плательщика — иначе гостевой путь
    // непроверяем везде, где провайдер мок.
    const plToken = params.get('pl') || '';
    const { user } = useAuth();
    const guest = !!plToken && !user;
    const [busy, setBusy] = useState(null); // 'success' | 'fail' | null
    const fetchStatus = React.useCallback(
      () => (guest
        ? subscriptionReal.getLinkPaymentStatus(plToken, invoiceId)
        : subscriptionReal.getPaymentStatus(invoiceId, ws)),
      [guest, plToken, invoiceId, ws],
    );
    // invoiceId здесь = provider_invoice_id (build_payment_url мок-провайдера) → GET /payments/<invoice_id>/.
    const { data: payment, loading, error, reload } =
      useFakeQuery('payment-' + invoiceId + (ws ? ':' + ws : '') + (guest ? ':g' : ''),
        fetchStatus, [fetchStatus]);

    // Куда возвращает кнопка «назад». У гостя «К тарифам» ведёт под
    // RequireAuth, то есть на /signin — на стаб-странице это выглядело бы как
    // сорванная оплата. Его единственный осмысленный адрес — сам счёт.
    const backPath = guest ? '/pay/' + encodeURIComponent(plToken) : '/settings?tab=subscription';
    const backLabel = guest ? 'Вернуться к счёту' : 'К тарифам';

    const kindLabel = (p) => (KIND_LABELS[p.kind] || p.kind);
    const planLabel = (p) => p.plan_label || PLAN_LABELS[p.plan_slug] || p.plan_slug;

    const act = (result) => {
      setBusy(result);
      // mock/callback/ требует payment_id (int pk) — берём из загруженного платежа, не invoice_id.
      subscriptionReal.mockPaymentCallback(payment.id, result)
        .then(() => {
          const q = '?invoice_id=' + encodeURIComponent(invoiceId)
            + (ws ? '&ws=' + encodeURIComponent(ws) : '')
            // Токен уносим дальше: без него страница успеха выкинет гостя на вход.
            + (plToken ? '&pl=' + encodeURIComponent(plToken) : '');
          navigate(result === 'success' ? '/checkout/success' + q : '/checkout/fail' + q);
        })
        .catch(() => setBusy(null));
    };

    let body;
    if (loading) {
      body = (
        <div className="checkout-state">
          <Icon name="spinner" size={28} />
          <p className="t-body text-dim">Загружаем платёж…</p>
        </div>
      );
    } else if (error) {
      body = (
        <div className="checkout-state">
          <div className="checkout-icon checkout-icon--bad"><Icon name="x-circle" size={26} /></div>
          <h2 className="t-h3">Платёж не найден</h2>
          <p className="t-body text-dim">Счёт недоступен или ссылка устарела.</p>
          <Button variant="secondary" onClick={() => navigate(backPath)}>{backLabel}</Button>
        </div>
      );
    } else {
      const closed = payment.status === 'succeeded' || payment.status === 'failed';
      const details = (
        <div className="checkout-details">
          <CheckoutRow label="Тариф" value={planLabel(payment)} />
          <CheckoutRow label="Сумма" value={fmt.money(payment.amount_rub)} mono tone="muted" />
          <CheckoutRow label="Счёт" value={payment.invoice_id} mono />
          <CheckoutRow label="Тип" value={kindLabel(payment)} />
        </div>
      );

      if (payment.status === 'succeeded') {
        body = (
          <>
            <header className="checkout-head">
              <div className="t-eyebrow text-dim">Заглушка платёжного шлюза</div>
              <h1 className="t-h2">Счёт уже оплачен</h1>
            </header>
            <div className="callout callout--ok checkout-banner">
              <Icon name="check-circle" size={18} />
              <span>Этот счёт оплачен ранее — повторная оплата не требуется.</span>
            </div>
            {details}
            <div className="checkout-actions">
              <Button variant="primary" className="full" onClick={() => navigate(backPath)}>{backLabel}</Button>
            </div>
          </>
        );
      } else if (closed) { // failed
        body = (
          <>
            <header className="checkout-head">
              <div className="t-eyebrow text-dim">Заглушка платёжного шлюза</div>
              <h1 className="t-h2">Счёт закрыт</h1>
            </header>
            <div className="callout callout--bad checkout-banner">
              <Icon name="x-circle" size={18} />
              <span>Оплата по этому счёту не прошла. Вернитесь к тарифам и попробуйте снова.</span>
            </div>
            {details}
            <div className="checkout-actions">
              <Button variant="primary" className="full" onClick={() => navigate(backPath)}>{backLabel}</Button>
            </div>
          </>
        );
      } else { // pending
        body = (
          <>
            <header className="checkout-head">
              <div className="t-eyebrow text-dim">Заглушка платёжного шлюза</div>
              <h1 className="t-h2">Оплата подписки</h1>
              <p className="t-body text-dim">Демо-режим: реального списания нет. Выберите исход.</p>
            </header>
            {details}
            <div className="checkout-actions">
              <Button variant="primary" className="full" loading={busy === 'success'} disabled={!!busy}
                      onClick={() => act('success')}>Симулировать оплату</Button>
              <Button variant="ghost" className="full checkout-btn-danger" loading={busy === 'fail'} disabled={!!busy}
                      onClick={() => act('fail')}>Отказаться</Button>
              <a className="link checkout-link" href={backPath}
                 onClick={(e) => { e.preventDefault(); navigate(backPath); }}>{backLabel}</a>
            </div>
          </>
        );
      }
    }

    return <CheckoutLayout>{body}</CheckoutLayout>;
  }

export { CheckoutMock };
